import { useState, useEffect, useCallback, useRef } from "react";
import { MdClose, MdOutlineKeyboardArrowLeft, MdOutlineKeyboardArrowRight } from "react-icons/md";

const DEFAULT_MANIFEST = "/images/galerie/manifest.json";
const DEFAULT_BASE = "/images/galerie/";

function Gallery({ title, images, manifest = DEFAULT_MANIFEST, basePath = DEFAULT_BASE }) {
    const [resolved, setResolved] = useState(images || null);
    const [current, setCurrent] = useState(0);
    const [lightbox, setLightbox] = useState(false);
    const intervalRef = useRef(null);

    useEffect(() => {
        if (images && images.length > 0) {
            setResolved(images);
            return;
        }
        let cancelled = false;
        fetch(manifest)
            .then((r) => (r.ok ? r.json() : []))
            .then((list) => {
                if (cancelled) return;
                const arr = Array.isArray(list) ? list : [];
                setResolved(arr.map((f) => (f.startsWith("/") || f.startsWith("http") ? f : basePath + f)));
            })
            .catch(() => !cancelled && setResolved([]));
        return () => { cancelled = true; };
    }, [images, manifest, basePath]);

    const list = resolved || [];

    const startAuto = useCallback(() => {
        if (intervalRef.current) clearInterval(intervalRef.current);
        intervalRef.current = setInterval(() => {
            setCurrent((i) => (i + 1) % list.length);
        }, 4000);
    }, [list.length]);

    const stopAuto = useCallback(() => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
    }, []);

    useEffect(() => {
        if (list.length < 2 || lightbox) return;
        startAuto();
        return stopAuto;
    }, [list.length, lightbox, startAuto, stopAuto]);

    const go = useCallback((dir) => {
        setCurrent((i) => (i + dir + list.length) % list.length);
    }, [list.length]);

    const closeLightbox = useCallback(() => setLightbox(false), []);

    useEffect(() => {
        if (!lightbox) return;
        const handler = (e) => {
            if (e.key === "Escape") closeLightbox();
            else if (e.key === "ArrowRight") setCurrent((i) => (i + 1) % list.length);
            else if (e.key === "ArrowLeft") setCurrent((i) => (i - 1 + list.length) % list.length);
        };
        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
    }, [lightbox, list.length, closeLightbox]);

    if (list.length === 0) return null;

    return (
        <div className="bg-white rounded-2xl p-6 lg:p-8">
            {title && <div className="text-3xl text-golden font-bold mb-6">{title}</div>}

            <div
                className="relative overflow-hidden rounded-xl select-none"
                onMouseEnter={stopAuto}
                onMouseLeave={() => { if (!lightbox) startAuto(); }}
            >
                <div className="relative w-full bg-gray-100" style={{ aspectRatio: "3 / 2" }}>
                    <img
                        key={current}
                        src={list[current]}
                        alt={`Galerie ${current + 1}`}
                        className="absolute inset-0 w-full h-full object-contain cursor-pointer"
                        onClick={() => setLightbox(true)}
                        draggable={false}
                    />
                </div>

                {list.length > 1 && (
                    <>
                        <button
                            type="button"
                            onClick={() => go(-1)}
                            className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/40 hover:bg-black/60 text-white rounded-full p-1 transition-colors"
                            aria-label="Vorheriges Bild"
                        >
                            <MdOutlineKeyboardArrowLeft size={36} />
                        </button>
                        <button
                            type="button"
                            onClick={() => go(1)}
                            className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/40 hover:bg-black/60 text-white rounded-full p-1 transition-colors"
                            aria-label="Nächstes Bild"
                        >
                            <MdOutlineKeyboardArrowRight size={36} />
                        </button>
                        <div className="absolute bottom-3 right-3 bg-black/50 text-white text-sm px-3 py-1 rounded-full">
                            {current + 1} / {list.length}
                        </div>
                    </>
                )}
            </div>

            {list.length > 1 && (
                <div className="flex justify-center gap-1.5 mt-4 flex-wrap">
                    {list.map((_, i) => (
                        <button
                            key={i}
                            type="button"
                            onClick={() => { stopAuto(); setCurrent(i); }}
                            className={`w-2 h-2 rounded-full transition-all ${i === current ? "bg-golden scale-125" : "bg-gray-300 hover:bg-gray-400"}`}
                            aria-label={`Bild ${i + 1}`}
                        />
                    ))}
                </div>
            )}

            {lightbox && (
                <div
                    className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center p-4"
                    onClick={closeLightbox}
                >
                    <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); closeLightbox(); }}
                        className="absolute top-4 right-4 text-white hover:text-golden"
                        aria-label="Schliessen"
                    >
                        <MdClose size={40} />
                    </button>
                    {list.length > 1 && (
                        <>
                            <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); setCurrent((i) => (i - 1 + list.length) % list.length); }}
                                className="absolute left-2 md:left-6 text-white hover:text-golden"
                                aria-label="Vorheriges Bild"
                            >
                                <MdOutlineKeyboardArrowLeft size={60} />
                            </button>
                            <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); setCurrent((i) => (i + 1) % list.length); }}
                                className="absolute right-2 md:right-6 text-white hover:text-golden"
                                aria-label="Nächstes Bild"
                            >
                                <MdOutlineKeyboardArrowRight size={60} />
                            </button>
                        </>
                    )}
                    <img
                        src={list[current]}
                        alt={`Galerie ${current + 1}`}
                        className="max-h-[90vh] max-w-[90vw] object-contain"
                        onClick={(e) => e.stopPropagation()}
                    />
                </div>
            )}
        </div>
    );
}

export default Gallery;
